import { useState } from "react";
import { transferDpp } from "@/lib/dppMutations";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface TransferDppFormProps {
  brand: string;
  model: string;
  serial: string;
  currentOwner?: string | null;
  onSuccess: () => void;
}

export function TransferDppForm({
  brand,
  model,
  serial,
  currentOwner,
  onSuccess,
}: TransferDppFormProps) {
  const [toEmail, setToEmail] = useState("");
  const [note, setNote] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  function handleReview(e: React.SyntheticEvent) {
    e.preventDefault();
    setError("");
    if (!toEmail.trim()) {
      setError("Enter the recipient's email address");
      return;
    }
    setConfirming(true);
  }

  async function handleConfirm() {
    setError("");
    setSubmitting(true);
    try {
      await transferDpp(brand, model, serial, toEmail.trim(), note.trim() || undefined);
      setSubmitted(true);
      onSuccess();
    } catch (err: unknown) {
      setError(apiErrorMessage(err, "Failed to start transfer"));
      setConfirming(false);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
        Transfer started. We've emailed{" "}
        <strong>{toEmail.trim()}</strong> a link to accept it. Ownership changes
        only after they accept.
      </div>
    );
  }

  if (confirming) {
    return (
      <div className="form">
        <div
          style={{
            fontSize: 13,
            color: "var(--ink-soft)",
            border: "1px solid var(--line)",
            padding: "14px 16px",
            lineHeight: 1.6,
          }}
        >
          <div style={{ marginBottom: 8 }}>
            Transfer the passport for{" "}
            <strong>
              {[brand, model].filter(Boolean).join(" · ")} · {serial}
            </strong>
          </div>
          <div>
            {currentOwner?.trim() ? (
              <>
                From <strong>{currentOwner.trim()}</strong>
                {" → "}
              </>
            ) : (
              <>To </>
            )}
            <strong>{toEmail.trim()}</strong>
          </div>
          <div style={{ marginTop: 8, color: "var(--ink-mute)" }}>
            The recipient must accept via an emailed link before ownership
            changes. You can cancel it from the Transfers page while it's
            pending.
          </div>
        </div>

        {error && (
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 12,
              color: "var(--danger)",
              border: "1px solid var(--danger)",
              padding: "10px 14px",
            }}
          >
            {error}
          </div>
        )}

        <div className="row gap-12">
          <Button type="button" onClick={handleConfirm} disabled={submitting}>
            {submitting ? "Starting…" : "Confirm transfer"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={() => setConfirming(false)}
            disabled={submitting}
          >
            Back
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="form" onSubmit={handleReview}>
      {currentOwner && (
        <div className="field">
          <Label>Current owner</Label>
          <Input value={currentOwner} disabled />
        </div>
      )}
      <div className="field">
        <Label htmlFor="transfer-email">Transfer to (email)</Label>
        <Input
          id="transfer-email"
          type="email"
          required
          placeholder="new.owner@example.com"
          value={toEmail}
          onChange={(e) => setToEmail(e.target.value)}
          disabled={submitting}
        />
      </div>
      <div className="field">
        <Label htmlFor="transfer-note">Note (optional)</Label>
        <Input
          id="transfer-note"
          placeholder="Internal note for this transfer"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={submitting}
        />
      </div>

      {error && (
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 12,
            color: "var(--danger)",
            border: "1px solid var(--danger)",
            padding: "10px 14px",
          }}
        >
          {error}
        </div>
      )}

      <Button type="submit">Review transfer</Button>
    </form>
  );
}
